import { useMemo, useState } from "react";
import { articles, gossipEventTypes, starNames, timeRanges } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import { withinDays } from "@/utils/format";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel, { type FilterGroup } from "@/components/feature/FilterPanel";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 6;

const filterGroups: FilterGroup[] = [
  {
    key: "star",
    label: "明星姓名",
    options: [{ label: "全部明星", value: "全部" }, ...starNames.map((name) => ({ label: name, value: name }))],
  },
  {
    key: "eventType",
    label: "事件类型",
    options: gossipEventTypes.map((type) => ({ label: type, value: type })),
  },
  {
    key: "time",
    label: "发布时间",
    options: timeRanges.map((range) => ({ label: range.label, value: range.value })),
  },
];

const gossipArticles = articles.filter((item) => item.category === "gossip");

export default function GossipPage() {
  usePageMeta({
    title: "八卦新闻 - 黑料吃瓜",
    description:
      "黑料吃瓜八卦频道汇总最新明星八卦新闻，涵盖恋情曝光、绯闻追踪与当事方澄清回应，支持按明星姓名、事件类型和时间段快速筛选。",
    keywords: "八卦新闻,娱乐八卦,明星恋情,绯闻,澄清,黑料吃瓜",
  });

  const [filters, setFilters] = useState<Record<string, string>>({
    star: "全部",
    eventType: "全部",
    time: "all",
  });
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const result = gossipArticles.filter((item) => {
      const starMatch = filters.star === "全部" || item.tags.includes(filters.star);
      const typeMatch = filters.eventType === "全部" || item.eventType === filters.eventType;
      return starMatch && typeMatch && withinDays(item.date, filters.time);
    });
    return result.sort((a, b) => (sort === "hot" ? b.views - a.views : a.date < b.date ? 1 : -1));
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const summary =
    [
      filters.star !== "全部" ? filters.star : "",
      filters.eventType !== "全部" ? filters.eventType : "",
      filters.time !== "all" ? `近 ${filters.time} 天` : "",
    ]
      .filter(Boolean)
      .join(" · ") || "全部内容";

  return (
    <div className="max-w-[1320px] mx-auto px-4 md:px-6 py-5 md:py-7">
      <Breadcrumb items={[{ label: "八卦频道" }]} />

      <header className="mt-3 mb-5 md:mb-6">
        <h1 className="font-heading text-2xl md:text-[30px] font-black text-foreground-950">明星八卦</h1>
        <p className="text-[13px] md:text-sm text-foreground-500 leading-7 mt-2 max-w-3xl">
          这里收录明星恋情、绯闻追踪与公开回应。每条内容都标注了发布时间与消息来源，方便你按人物或事件类型回看完整时间线。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[256px_minmax(0,1fr)] gap-5 md:gap-6">
        <aside>
          <FilterPanel groups={filterGroups} values={filters} onChange={handleFilter} summary={summary} />
        </aside>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3.5">
            <p className="text-[13px] text-foreground-600">
              共 <span className="text-accent-600 font-bold">{filtered.length}</span> 条相关内容
              <span className="hidden sm:inline text-foreground-400"> · 第 {currentPage} / {totalPages} 页</span>
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              {[
                { label: "最新发布", value: "latest" },
                { label: "最多阅读", value: "hot" },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSort(option.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1 rounded-full text-[12.5px] whitespace-nowrap transition-colors cursor-pointer ${
                    sort === option.value
                      ? "bg-background-50 text-foreground-900 font-medium"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {visible.map((article) => (
                  <ArticleCard key={article.id} article={article} showViews />
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-300" />
              <p className="font-heading text-base font-bold text-foreground-800 mt-3">没有符合条件的八卦</p>
              <p className="text-[13px] text-foreground-500 mt-1.5">试着放宽明星姓名或时间段，会看到更多内容。</p>
              <button
                type="button"
                onClick={() => {
                  setFilters({ star: "全部", eventType: "全部", time: "all" });
                  setPage(1);
                }}
                className="inline-flex items-center gap-1.5 mt-4 px-4 h-9 rounded-md bg-primary-500 text-background-50 text-[13px] whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}